'use client'

import { motion } from 'motion/react'
import Image from 'next/image'
import { useLocale } from 'next-intl'
import { Button } from '@/components/button'
import { Motion } from '@/components/motion'
import { T } from '@/components/t'
import { Locale } from '@/setup/locales'
import { ExternalDeploymentUrl } from '@/setup/urls/external-deployment-urls'

export const JobsHero = () => {
    const locale = useLocale()

    const url = {
        href: {
            externalUrl: ExternalDeploymentUrl.JOBS[locale as Locale],
        },
    } as const

    return (
        <section className="flex justify-between md:gap-6 w-full md:w-[90%] max-w-[1040px] mb-10 md:mb-24 xl:mb-52">
            <div className="mt-14 md:mt-0 mb-8 md:mb-0 flex flex-col justify-center items-center md:items-start md:justify-start md:pt-24 w-full md:w-2/5">
                <Motion baseDelay={0.25} y={55} className="relative" style={{ zIndex: 20 }}>
                    <div>
                        <h1 className="text-center md:text-left text-[36px] md:text-[40px] lg:text-[44px] xl:text-[48px] font-bold mb-4 text-black px-4 md:px-0">
                            <T span t={'jobs.hero.title'} className="z-20" />
                        </h1>
                    </div>
                </Motion>

                <Motion baseDelay={0.45} y={70} className="mb-6 hidden md:block">
                    <T
                        p
                        t={'jobs.hero.description'}
                        className="font-normal text-black/70 max-w-[700px] text-[14px] md:text-[16px]"
                    />
                </Motion>
                <Motion baseDelay={0.45} y={70} className="mb-6 px-4 md:px-0">
                    <Button
                        shape="pill"
                        width="max-content"
                        height="46px"
                        styleType="primary"
                        href={url.href}
                        target="_blank"
                    >
                        <T t="jobs.hero.cta" />
                    </Button>
                </Motion>
            </div>
            <div className="hidden md:flex h-full w-3/5 gap-6 justify-end">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{
                        opacity: 0,
                        y: -20,
                        transition: { duration: 0.1, ease: 'easeIn' },
                    }}
                    transition={{
                        opacity: { duration: 0.38, ease: 'easeOut', delay: 0.18 },
                        y: { type: 'spring', stiffness: 500, damping: 70, delay: 0.18 },
                    }}
                    key={'jobs-hero-image-1'}
                    className="w-full flex items-center justify-center"
                >
                    <div className="flex items-start pt-24 justify-center">
                        <Image
                            src={'/_images/jobs/hero/jobs-computer.webp'}
                            alt="Employee"
                            width={219}
                            height={323}
                            priority={true}
                            className="rounded-l overflow-hidden md:h-[323px] md:w-[219px] h-[323px] w-[219px] object-cover"
                        />
                    </div>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{
                        opacity: 0,
                        y: -20,
                        transition: { duration: 0.1, ease: 'easeIn' },
                    }}
                    transition={{
                        opacity: { duration: 0.38, ease: 'easeOut', delay: 0.18 },
                        y: { type: 'spring', stiffness: 500, damping: 70, delay: 0.18 },
                    }}
                    key={'jobs-hero-image-2'}
                    className="w-full flex items-center justify-center"
                >
                    <div className="flex flex-col gap-6">
                        <Image
                            src={'/_images/jobs/hero/employee-julia.webp'}
                            alt="Employee"
                            width={176}
                            height={244}
                            priority={true}
                            className="rounded-l overflow-hidden md:h-[244px] md:w-[176px] h-[244px] w-[176px] object-cover"
                        />
                        <Image
                            src={'/_images/jobs/hero/office-hero.webp'}
                            alt="Employee"
                            width={389}
                            height={308}
                            priority={true}
                            className="rounded-l overflow-hidden md:h-[308px] md:w-[389px] h-[308px] w-[389px] object-cover"
                        />
                    </div>
                </motion.div>
            </div>
        </section>
    )
}
